import { t } from "@lingui/macro";
import { 
  FileText, 
  ArrowCounterClockwise, 
  FrameCorners, 
  ArrowsOutLineHorizontal, 
  Sliders, 
  Scissors, 
  ListNumbers, 
  CheckCircle 
} from "@phosphor-icons/react";
import { defaultResumeData } from "@stylingcv/schema";
import { Button, Slider, Tooltip } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";

import { useResumeStore } from "@/client/stores/resume";

export const PageSection = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const page = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.page || defaultResumeData.metadata.page;
    } catch {
      return defaultResumeData.metadata.page;
    }
  });

  const updatePage = (path: string, value: unknown) => {
    try {
      setValue(`metadata.page.${path}`, value);
    } catch {
      // Silent fail
    }
  };

  const onResetPage = () => {
    try {
      setValue("metadata.page", defaultResumeData.metadata.page);
    } catch {}
  };

  const margin = page.margin || 25;
  const isA4 = (page.format || "a4") === "a4";
  const isLetter = (page.format || "a4") === "letter";
  const showBreakLine = page.options?.breakLine ?? false;
  const showPageNumbers = page.options?.pageNumbers ?? false;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-5">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-gray-100">
            <FileText size={20} className="text-gray-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#16344c]">{t`Page`}</h3>
            <p className="text-[11px] text-gray-400">{t`Format · margins · footer`}</p>
          </div>
        </div>
        <Tooltip content={t`Reset Page Settings`}>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-[#16344c] transition-colors" onClick={onResetPage}>
            <ArrowCounterClockwise size={18} />
          </Button>
        </Tooltip>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-6">

      {/* Format Selection */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FrameCorners size={18} weight="duotone" className="text-[#268599]" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t`Format`}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* A4 Button */}
          <button
            onClick={() => updatePage("format", "a4")}
            className={cn(
              "flex flex-col items-center justify-center py-5 px-2 rounded-xl border transition-all group overflow-hidden relative",
              isA4 ? "bg-[#268599]/10 border-[#268599]" : "bg-white border-gray-200 hover:border-gray-300"
            )}>
            <div className={cn(
              "w-10 h-14 border-2 border-dashed flex items-center justify-center rounded-sm mb-3 transition-colors",
              isA4 ? "border-[#268599] text-[#268599]" : "border-gray-300 text-gray-400"
            )}>
              <span className="text-[10px] font-bold">A4</span>
            </div>
            <span className={cn("text-xs font-bold", isA4 ? "text-[#16344c]" : "text-gray-500")}>{t`A4 Format`}</span>
            <span className="text-[9px] text-gray-400 mt-1">210 x 297 mm</span>

            <div className={cn("absolute inset-0 bg-[#268599]/5 transition-opacity", isA4 ? "opacity-0" : "opacity-0 group-hover:opacity-100")} />
            <CheckCircle weight="fill" className={cn("absolute top-2 right-2 text-[#268599] transition-opacity", isA4 ? "opacity-100" : "opacity-0")} />
          </button>

          {/* Letter Button */}
          <button
            onClick={() => updatePage("format", "letter")}
            className={cn(
              "flex flex-col items-center justify-center py-5 px-2 rounded-xl border transition-all group overflow-hidden relative",
              isLetter ? "bg-[#268599]/10 border-[#268599]" : "bg-white border-gray-200 hover:border-gray-300"
            )}>
            <div className={cn(
              "w-11 h-13 border-2 border-dashed flex items-center justify-center rounded-sm mb-3 transition-colors",
              isLetter ? "border-[#268599] text-[#268599]" : "border-gray-300 text-gray-400"
            )}>
              <span className="text-[10px] font-bold">LTR</span>
            </div>
            <span className={cn("text-xs font-bold", isLetter ? "text-[#16344c]" : "text-gray-500")}>{t`US Letter`}</span>
            <span className="text-[9px] text-gray-400 mt-1">216 x 279 mm</span>

            <div className={cn("absolute inset-0 bg-[#268599]/5 transition-opacity", isLetter ? "opacity-0" : "opacity-0 group-hover:opacity-100")} />
            <CheckCircle weight="fill" className={cn("absolute top-2 right-2 text-[#268599] transition-opacity", isLetter ? "opacity-100" : "opacity-0")} />
          </button>
        </div>
      </div>

      <div className="h-px bg-gray-100 w-full"></div>

      {/* Margin Slider */}
      <div className="space-y-4">
        <div className="flex items-center justify-between text-gray-500">
          <div className="flex items-center gap-2">
            <ArrowsOutLineHorizontal size={18} weight="duotone" className="text-[#268599]" />
            <span className="text-xs font-semibold uppercase tracking-wider">{t`Page Margin`}</span>
          </div>
          <span className="text-xs font-mono font-bold text-[#268599] bg-[#268599]/10 px-2 py-0.5 rounded border border-[#268599]/20">
            {margin} px
          </span>
        </div>

        <Slider
          min={0}
          max={60}
          step={2}
          value={[margin]}
          onValueChange={([value]) => updatePage("margin", value)}
          className="[&>[role=slider]]:bg-[#268599] [&>[role=slider]]:border-[#268599] [&>.relative>.absolute]:bg-[#268599]"
        />

        {/* Visual Margin Preview Mockup */}
        <div className="w-full h-24 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center relative overflow-hidden">
          <div
            className="absolute bg-white border border-[#268599]/30 rounded-sm transition-all duration-300"
            style={{ top: `${margin/2}px`, bottom: `${margin/2}px`, left: `${margin/2}px`, right: `${margin/2}px` }}
          >
            <div className="w-full h-full flex flex-col justify-between p-2">
              <div className="w-1/3 h-1 bg-gray-300 rounded"></div>
              <div className="space-y-1">
                <div className="w-full h-1 bg-gray-200 rounded"></div>
                <div className="w-4/5 h-1 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-px bg-gray-100 w-full"></div>

      {/* Advanced Toggles */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Sliders size={18} weight="duotone" className="text-[#268599]" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t`Options`}</span>
        </div>

        {/* Show Break Line Toggle */}
        <div
          className="flex items-center justify-between p-3 rounded-xl bg-white border border-gray-200 hover:border-[#268599]/30 transition-colors cursor-pointer group"
          onClick={() => updatePage("options.breakLine", !showBreakLine)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center text-gray-400 group-hover:text-[#16344c] transition-colors">
              <Scissors size={18} />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-[#16344c]">{t`Show Break Line`}</span>
              <span className="text-[10px] text-gray-400">{t`Display cut indicator for pages`}</span>
            </div>
          </div>

          <div className={cn("relative w-10 h-5 rounded-full transition-colors duration-300", showBreakLine ? "bg-[#268599]" : "bg-gray-200")}>
            <div className={cn("absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-300", showBreakLine ? "translate-x-5" : "translate-x-0")} />
          </div>
        </div>

        {/* Show Page Numbers Toggle */}
        <div
          className="flex items-center justify-between p-3 rounded-xl bg-white border border-gray-200 hover:border-[#268599]/30 transition-colors cursor-pointer group"
          onClick={() => updatePage("options.pageNumbers", !showPageNumbers)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center text-gray-400 group-hover:text-[#16344c] transition-colors">
              <ListNumbers size={18} />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-[#16344c]">{t`Show Page Numbers`}</span>
              <span className="text-[10px] text-gray-400">{t`Add numbers to footers`}</span>
            </div>
          </div>

          <div className={cn("relative w-10 h-5 rounded-full transition-colors duration-300", showPageNumbers ? "bg-[#268599]" : "bg-gray-200")}>
            <div className={cn("absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-300", showPageNumbers ? "translate-x-5" : "translate-x-0")} />
          </div>
        </div>
      </div>

      </div>

    </div>
  );
};
